import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { api, PRODUCT_LABEL, SOURCE_LABEL, STAGES } from "../lib/api";

export function LeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const leadId = Number(id);
  const qc = useQueryClient();
  const leadQ = useQuery({ queryKey: ["lead", leadId], queryFn: () => api.getLead(leadId) });

  const setStage = useMutation({
    mutationFn: (stage: string) => api.setStage(leadId, stage),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lead", leadId] }),
  });
  const updateFin = useMutation({
    mutationFn: (patch: Record<string, unknown>) => api.updateFinance(leadId, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lead", leadId] }),
  });
  const addNote = useMutation({
    mutationFn: (text: string) => api.addNote(leadId, text),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lead", leadId] }),
  });
  const createTask = useMutation({
    mutationFn: (p: { title: string; due_at: string | null }) =>
      api.createTask(leadId, p.title, p.due_at),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lead", leadId] }),
  });
  const toggleTask = useMutation({
    mutationFn: (p: { id: number; done: boolean }) => api.toggleTask(p.id, p.done),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lead", leadId] }),
  });

  const [noteText, setNoteText] = useState("");
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDue, setTaskDue] = useState("");
  const [amount, setAmount] = useState<string>("");
  const [editingAmount, setEditingAmount] = useState(false);

  if (leadQ.isLoading) return <div>Загрузка…</div>;
  if (leadQ.error || !leadQ.data) return <div className="error">Ошибка: {String(leadQ.error)}</div>;

  const l = leadQ.data;

  return (
    <>
      <div className="toolbar">
        <div>
          <Link to="/">← Доска</Link>
          <h2 style={{ margin: "8px 0 0" }}>{l.name || `Лид #${l.lead_id}`}</h2>
        </div>
        <select
          value={l.stage}
          onChange={(e) => setStage.mutate(e.target.value)}
        >
          {STAGES.map((s) => (
            <option key={s.key} value={s.key}>{s.title}</option>
          ))}
        </select>
      </div>

      <div className="lead-detail">
        <div>
          <div className="panel">
            <h3>Переписка</h3>
            {l.messages.length === 0 && <div style={{ color: "var(--muted)" }}>Сообщений нет</div>}
            {l.messages.map((m) => (
              <div key={m.id} className={`msg ${m.direction === "out" ? "out" : "in"}`}>
                {m.text || <em style={{ opacity: 0.5 }}>[пусто]</em>}
                <div style={{ fontSize: 10, opacity: 0.6, marginTop: 4 }}>
                  {new Date(m.created_at).toLocaleString("ru-RU")}
                </div>
              </div>
            ))}
          </div>

          <div className="panel" style={{ marginTop: 16 }}>
            <h3>Заметки</h3>
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <input
                style={{ flex: 1 }}
                placeholder="Добавить заметку…"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
              />
              <button
                className="primary"
                disabled={!noteText.trim()}
                onClick={() => { addNote.mutate(noteText); setNoteText(""); }}
              >
                +
              </button>
            </div>
            {l.notes.map((n) => (
              <div key={n.id} className="note">
                {n.text}
                <div className="meta">
                  @{n.user_username || "?"} · {new Date(n.created_at).toLocaleString("ru-RU")}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="panel">
            <h3>Карточка</h3>
            <div className="kv">
              <div>Телефон</div><div>{l.phone || "—"}</div>
              <div>Email</div><div>{l.email || "—"}</div>
              <div>Источник</div><div>{SOURCE_LABEL[l.source]}</div>
              <div>Продукт</div>
              <div>
                <select
                  value={l.product}
                  onChange={(e) => updateFin.mutate({ product: e.target.value })}
                  style={{ width: "100%" }}
                >
                  {Object.entries(PRODUCT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>Сумма</div>
              <div>
                {editingAmount ? (
                  <span style={{ display: "flex", gap: 4 }}>
                    <input value={amount} onChange={(e) => setAmount(e.target.value)} style={{ width: 100 }} />
                    <button
                      className="primary"
                      onClick={() => {
                        const v = parseFloat(amount);
                        updateFin.mutate({ deal_amount: isFinite(v) ? v : null });
                        setEditingAmount(false);
                      }}
                    >OK</button>
                  </span>
                ) : (
                  <span
                    onClick={() => { setAmount(String(l.deal_amount ?? "")); setEditingAmount(true); }}
                    style={{ cursor: "pointer", borderBottom: "1px dotted var(--muted)" }}
                  >
                    {l.deal_amount != null ? Number(l.deal_amount).toLocaleString("ru-RU") : "—"}
                  </span>
                )}
              </div>
              <div>Оплата</div>
              <div>
                <select
                  value={l.payment_status}
                  onChange={(e) => updateFin.mutate({ payment_status: e.target.value })}
                  style={{ width: "100%" }}
                >
                  <option value="none">нет</option>
                  <option value="pending">ожидается</option>
                  <option value="partial">частичная</option>
                  <option value="paid">оплачено</option>
                  <option value="refunded">возврат</option>
                </select>
              </div>
              <div>Менеджер</div>
              <div>{l.assignee_username ? `@${l.assignee_username}` : "—"}</div>
            </div>
          </div>

          <div className="panel" style={{ marginTop: 16 }}>
            <h3>Задачи</h3>
            <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
              <input
                placeholder="Новая задача…"
                value={taskTitle}
                onChange={(e) => setTaskTitle(e.target.value)}
                style={{ flex: "1 1 160px" }}
              />
              <input type="datetime-local" value={taskDue} onChange={(e) => setTaskDue(e.target.value)} />
              <button
                className="primary"
                disabled={!taskTitle.trim()}
                onClick={() => {
                  createTask.mutate({ title: taskTitle, due_at: taskDue ? new Date(taskDue).toISOString() : null });
                  setTaskTitle(""); setTaskDue("");
                }}
              >+</button>
            </div>
            {l.tasks.map((t) => (
              <div key={t.id} className={`task ${t.done_at ? "done" : ""}`}>
                <span>
                  <input
                    type="checkbox"
                    checked={Boolean(t.done_at)}
                    onChange={(e) => toggleTask.mutate({ id: t.id, done: e.target.checked })}
                    style={{ marginRight: 8 }}
                  />
                  {t.title}
                </span>
                <span style={{ fontSize: 11, color: "var(--muted)" }}>
                  {t.due_at ? new Date(t.due_at).toLocaleString("ru-RU") : ""}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
